import { createFileRoute, Link } from "@tanstack/react-router";
import { DollarSign, ShoppingBag, ClipboardList, Users, ArrowRight } from "lucide-react";
import { useState, useEffect } from "react";
import { API_BASE_URL } from "@/lib/api";

export const Route = createFileRoute("/admin/")({
  head: () => ({
    meta: [
      { title: "Admin Dashboard — Royal Lumina" },
    ],
  }),
  component: AdminDashboard,
});

// Mock initial data for UI display (falls back if API is offline)
const mockStats = {
  totalRevenue: 34850,
  orderCount: 42,
  productCount: 12,
  customerCount: 29,
  recentOrders: [
    { id: "ord_101", customer: "Aarav Sharma", date: "2026-07-04", total: 1798, status: "processing" },
    { id: "ord_102", customer: "Priya Patel", date: "2026-07-03", total: 649, status: "shipped" },
    { id: "ord_103", customer: "Kabir Mehta", date: "2026-07-03", total: 2398, status: "delivered" },
    { id: "ord_104", customer: "Ananya Iyer", date: "2026-07-02", total: 1299, status: "pending" },
  ]
};

function AdminDashboard() {
  const [stats, setStats] = useState(mockStats);
  const [loading, setLoading] = useState(false);

  // We can fetch stats from backend if online
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem("supabase.auth.token") || ""; // We will hook up real token later
        const res = await fetch(`${API_BASE_URL}/api/users/stats`, {
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          // Map backend stats to dashboard
          setStats({
            totalRevenue: data.totalRevenue || mockStats.totalRevenue,
            orderCount: data.orderCount || mockStats.orderCount,
            productCount: data.productCount || mockStats.productCount,
            customerCount: data.customerCount || mockStats.customerCount,
            recentOrders: data.recentOrders ? data.recentOrders.map((o: any) => ({
              id: o.id,
              customer: o.user?.name || o.user?.email || "Guest Customer",
              date: new Date(o.created_at).toLocaleDateString(),
              total: o.totalAmount,
              status: o.status
            })) : mockStats.recentOrders
          });
        }
      } catch (err) {
        // silent fallback
      }
    };
    fetchStats();
  }, []);

  const cards = [
    { label: "Total Revenue", value: `₹${stats.totalRevenue.toLocaleString()}`, icon: DollarSign, color: "text-amber-600 bg-amber-50" },
    { label: "Orders", value: stats.orderCount, icon: ClipboardList, color: "text-blue-600 bg-blue-50" },
    { label: "Products", value: stats.productCount, icon: ShoppingBag, color: "text-emerald-600 bg-emerald-50" },
    { label: "Customers", value: stats.customerCount, icon: Users, color: "text-purple-600 bg-purple-50" },
  ];

  return (
    <div className="space-y-10">
      <div>
        <h2 className="font-display text-4xl tracking-tight text-primary">Dashboard Overview</h2>
        <p className="text-sm text-muted-foreground mt-1">Real-time metrics and customer activity at the atelier.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((c) => (
          <div key={c.label} className="bg-card border border-border rounded-[24px] p-6 shadow-luxe transition-all hover:shadow-luxe-hover">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{c.label}</span>
              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${c.color}`}>
                <c.icon className="h-5 w-5" />
              </div>
            </div>
            <div className="mt-4 font-display text-3xl sm:text-4xl text-primary font-semibold">
              {c.value}
            </div>
          </div>
        ))}
      </div>

      {/* Recent Orders Table */}
      <div className="bg-card border border-border rounded-[24px] overflow-hidden shadow-luxe">
        <div className="px-6 py-5 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl text-primary">Recent Orders</h3>
            <p className="text-xs text-muted-foreground mt-0.5">The latest orders placed by candle lovers.</p>
          </div>
          <Link to="/admin/orders" className="text-xs uppercase tracking-wider text-gold hover:text-gold/80 flex items-center gap-1 font-semibold">
            <span>View All</span>
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="bg-muted/40 border-b border-border text-[0.65rem] uppercase tracking-wider text-muted-foreground font-semibold">
                <th className="px-6 py-4">Order ID</th>
                <th className="px-6 py-4">Customer</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4 text-right">Total</th>
                <th className="px-6 py-4 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {stats.recentOrders.map((order) => (
                <tr key={order.id} className="hover:bg-muted/10 transition-colors">
                  <td className="px-6 py-4 font-mono text-xs">{order.id}</td>
                  <td className="px-6 py-4 font-medium">{order.customer}</td>
                  <td className="px-6 py-4 text-muted-foreground">{order.date}</td>
                  <td className="px-6 py-4 text-right font-medium">₹{order.total.toLocaleString()}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-[0.65rem] font-bold uppercase tracking-wider ${
                      order.status === "delivered" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                      order.status === "shipped" ? "bg-blue-50 text-blue-700 border border-blue-200" :
                      order.status === "processing" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                      "bg-rose-50 text-rose-700 border border-rose-200"
                    }`}>
                      {order.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
